import { useMutation, useQueryClient } from '@tanstack/react-query';
import { uniq } from 'lodash-es';
import { customAlphabet } from 'nanoid/non-secure';
import type { ConversationFormDataType } from '@/components/conversations/conversation-form';
import { generateSessionSettingsQueryOptions } from '@/hooks/sessions/use-session-settings';
import {
  ConversationMessageContentType,
  type ConversationMessageTypeUnion,
  conversationGroupMessageScheme,
  conversationMessageScheme,
} from '@/models/conversations/conversation';
import type { ConversationType } from '@/models/conversations/conversation-session';
import {
  type SendChatMessageType,
  type SendGroupChatMessageType,
  SendMessageCommand,
} from '@/models/messages/send-message';
import { generateResponseScheme, type RespondedWith, ResponseError } from '@/models/response';
import { SENTRY_LOG, simpleLogger } from '@/services/sentry';
import { getUserId } from '@/stores/auth';
import { useMessageStore } from '@/stores/message';
import { addToast } from '@/stores/toast';
import { fetcher } from '@/utils/fetcher';
import { getDeviceIdentifier } from '@/utils/get-standalone-mode';

export const alphaNumericalNanoId = customAlphabet(
  '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz',
  20,
);

export function generateClientMessageId(message: SendChatMessageType | SendGroupChatMessageType): string {
  const type = message.Cmd === SendMessageCommand.Chat ? 'session' : 'group';
  const id = message.Cmd === SendMessageCommand.Chat ? message.ChatSessionID : message.GroupID;
  const deviceType = getDeviceIdentifier();

  return `D${deviceType}U${message.UserID}${type === 'session' ? 'C' : 'G'}${id}-${message.ClientMsgId}`;
}

export function generateMessagePackage(
  type: ConversationType,
  id: Nullable<number>,
  data: ConversationFormDataType,
): SendChatMessageType | SendGroupChatMessageType {
  const userId = getUserId();
  if (!userId || !id) throw 'Invalid user id';

  const nowTime = Date.now();
  const message: SendChatMessageType | SendGroupChatMessageType =
    type === 'session'
      ? {
          Cmd: SendMessageCommand.Chat,
          Message: data.Content ?? '',
          UserID: userId,
          ChatSessionID: id,
          QuoteChatID: data.Quote?.ID,
          ClientMsgId: alphaNumericalNanoId(),
          MsgType: ConversationMessageContentType.Text,
          TimeStamp: nowTime,
        }
      : {
          Cmd: SendMessageCommand.GroupChat,
          Message: data.Content ?? '',
          UserID: userId,
          GroupID: id,
          QuoteGroupChatID: data.Quote?.ID,
          ClientMsgId: alphaNumericalNanoId(),
          MsgType: ConversationMessageContentType.Text,
          TimeStamp: nowTime,
          AtIDs: data.AtIDs ? uniq(data.AtIDs).join('|') : null,
        };

  if ('ContentType' in data && data.ContentType) {
    message.MsgType = data.ContentType;
  }

  return message;
}

export default function useSendMessage() {
  const queryClient = useQueryClient();

  return useMutation<
    RespondedWith<ConversationMessageTypeUnion>,
    ResponseError,
    SendChatMessageType | SendGroupChatMessageType
  >({
    mutationKey: ['/SendMessage'],
    mutationFn: (message) => {
      if (message.ClientMsgId === undefined) {
        message.ClientMsgId = generateClientMessageId(message);
      }
      simpleLogger({ name: SENTRY_LOG.SEND_MESSAGE_API, requestAttributes: { message: JSON.stringify(message) } });

      if (message.Cmd === SendMessageCommand.Chat) {
        return fetcher(
          '/SendChat',
          {
            ChatSessionID: message.ChatSessionID,
            Message: message.Message,
            MsgType: message.MsgType,
            TimeStamp: message.TimeStamp,
            QuoteChatID: message?.QuoteChatID,
            ClientMsgId: message.ClientMsgId,
          },
          {
            validResponseCodes: [0],
            schema: generateResponseScheme(conversationMessageScheme),
          },
        );
      }
      if (message.Cmd === SendMessageCommand.GroupChat) {
        return fetcher(
          '/SendGroupChat',
          {
            GroupID: message.GroupID,
            Message: message.Message,
            MsgType: message.MsgType,
            TimeStamp: message.TimeStamp,
            AtIDs: message.AtIDs,
            QuoteGroupChatID: message.QuoteGroupChatID,
            ClientMsgId: message.ClientMsgId,
          },
          {
            validResponseCodes: [0],
            schema: generateResponseScheme(conversationGroupMessageScheme),
          },
        );
      }

      return Promise.reject(new ResponseError(-1, 'Invalid message type'));
    },
    onSuccess: (data, message) => {
      const type = message.Cmd === SendMessageCommand.Chat ? 'session' : 'group';
      const id = message.Cmd === SendMessageCommand.Chat ? message.ChatSessionID : message.GroupID;

      if (data.Code === 0 && data.Data && id) {
        useMessageStore.getState().validate(type, id, data.Data.TimeStamp, data.Data.ID);
      }
    },
    onError: async (error, message) => {
      const isGroup = message.Cmd === SendMessageCommand.GroupChat;
      const type = isGroup ? 'group' : 'session';
      const id = isGroup ? message.GroupID : message.ChatSessionID;

      await queryClient.refetchQueries(generateSessionSettingsQueryOptions(type, id));

      addToast({ type: 'warning', message: error.message });

      if (error.code === 3) {
        useMessageStore.getState().delete(message.TimeStamp, type);
      }
    },
  });
}
